// ============================================================================
// menu.js — ESTRUTURA DO MENU (fonte única da navegação)
// ----------------------------------------------------------------------------
// Cada nó tem uma `key` ESTÁVEL. Essa key é o identificador de permissão usado
// em três lugares:
//   1. sidebar  (mostrar/esconder o item)
//   2. rotas    (<Private menuKey="...">)
//   3. banco    (operacional.tipo_usuario_permissoes.menu_key)
//
// `roles` = papéis que enxergam o item POR PADRÃO. É o fallback quando a API de
// permissões não responde, e a origem do seed do banco. Depois que o RBAC está
// no ar, quem manda é a tabela — editável pela tela /manutencao/papeis.
//
// `hidden: true` = a tela NÃO aparece na sidebar, mas continua no catálogo de
// permissões. Serve para rota que existe e precisa ser controlada, porém é
// alcançada por dentro de outra tela (detalhe, variante, link direto).
//
// De onde vieram os `roles`: da INTERSEÇÃO entre o que a sidebar antiga
// mostrava e o que a rota antiga aceitava em requiredPermissions. Ou seja, o
// padrão reproduz o acesso EFETIVO de hoje — ninguém ganha nem perde tela na
// migração. Onde a sidebar era mais restrita que a rota (ex.: Pré-Entrada em
// Lote, que o navbar escondia de supDoc por estar dentro do grupo Gate), o
// padrão seguiu a sidebar. Ajuste na tela de papéis, não aqui.
//
// REGRA: nunca mude uma `key` já existente (quebra o vínculo com o banco).
// Item novo → key nova. Item removido → remova também do banco.
// ============================================================================

// Todos os papéis operacionais. Usado nos itens que qualquer um logado abre.
const ALL_OPE = [
  'dev', 'admin', 'gGeral', 'gerencPatio', 'supDoc', 'documental',
  'agendamento', 'gate', 'supAva', 'avaria', 'financeiro',
  'vistoria', 'conferente', 'oficina',
];

// Quem já entrava na Home hoje (requiredPermissions da rota `/home`).
const HOME_ROLES = [
  'dev', 'admin', 'gGeral', 'documental', 'supDoc',
  'avaria', 'supAva', 'agendamento', 'financeiro', 'gate',
];

export const MENU = [
  {
    key: 'home',
    label: 'Home',
    icon: 'bi-house-door',
    path: '/home',
    roles: HOME_ROLES,
  },

  {
    key: 'cadastros',
    label: 'Cadastros',
    icon: 'bi-person-plus',
    path: '/manutencao/entidades',
    roles: ['dev', 'admin', 'gGeral', 'supDoc', 'supAva', 'agendamento', 'documental', 'avaria', 'financeiro'],
  },

  {
    key: 'gate',
    label: 'Gate',
    icon: 'bi-box-arrow-in-right',
    children: [
      {
        key: 'gate.pre_entrada',
        label: 'Pré-Entrada (Aprovações)',
        icon: 'bi-check2-circle',
        path: '/pre_entrada',
        roles: ['dev', 'admin', 'gGeral', 'gate'],
      },
      {
        key: 'gate.pre_entrada_lote',
        label: 'Pré-Entrada em Lote',
        icon: 'bi-collection',
        path: '/pre_entrada_lote',
        roles: ['dev', 'admin', 'gGeral'],
      },
      {
        key: 'gate.gate_in',
        label: 'Gate In (Entrada)',
        icon: 'bi-box-arrow-in-down',
        path: '/gate_in',
        roles: ['dev', 'admin'],
      },
      {
        key: 'gate.gate_out',
        label: 'Gate Out (Saída)',
        icon: 'bi-box-arrow-up',
        path: '/gate_out',
        roles: ['dev', 'admin'],
      },
      {
        key: 'gate.carregamento',
        label: 'Carregamento',
        icon: 'bi-truck',
        path: '/carregamento',
        roles: ['dev', 'admin'],
      },
      {
        key: 'gate.open_turn_in',
        label: 'Open Turn In',
        icon: 'bi-door-open',
        path: '/open_turn_in',
        hidden: true,
        roles: ['dev', 'admin', 'gGeral', 'documental', 'supDoc', 'gate'],
      },
    ],
  },

  {
    key: 'avaria',
    label: 'Avaria',
    icon: 'bi-exclamation-triangle',
    children: [
      {
        key: 'avaria.sobre_rodas',
        label: 'Vistoria Sobre Rodas',
        icon: 'bi-clipboard-check',
        path: '/sobre_rodas',
        roles: ['dev', 'admin', 'gGeral', 'supAva', 'avaria'],
      },
      {
        key: 'avaria.controle_cobrancas',
        label: 'Controle de Cobranças',
        icon: 'bi-cash-coin',
        path: '/controle_cobrancas',
        roles: ['dev', 'admin', 'gGeral', 'supAva', 'avaria'],
      },
      {
        key: 'avaria.lancamento_cobrancas',
        label: 'Lançamento de Cobranças',
        icon: 'bi-plus-circle',
        path: '/lancamento_cobrancas',
        roles: ['dev', 'admin', 'gGeral', 'supAva', 'avaria'],
      },
      {
        key: 'avaria.special_condition',
        label: 'Special Condition',
        icon: 'bi-star',
        path: '/special_condition',
        roles: ['dev', 'admin', 'gGeral', 'supAva', 'avaria'],
      },
      {
        key: 'avaria.painel_bloqueio',
        label: 'Painel Bloqueio',
        icon: 'bi-slash-circle',
        path: '/painel_bloqueio',
        roles: ['dev', 'admin', 'gGeral', 'supAva', 'avaria'],
      },
      {
        key: 'avaria.cobranca_lote',
        label: 'Cobranças em Lote',
        icon: 'bi-collection',
        path: '/cobranca_lote',
        roles: ['dev'],
      },
      {
        key: 'avaria.pagamento_lote',
        label: 'Pagamento em Lote',
        icon: 'bi-wallet2',
        path: '/pagamento_lote',
        hidden: true,
        roles: ['dev', 'admin'],
      },
      {
        key: 'avaria.contatos_transp',
        label: 'Contatos de Transportadoras',
        icon: 'bi-telephone',
        path: '/contatos_transp',
        hidden: true,
        roles: ['dev', 'admin', 'gGeral', 'supAva', 'avaria'],
      },
    ],
  },

  {
    key: 'logistica',
    label: 'Logística',
    icon: 'bi-diagram-3',
    children: [
      {
        key: 'logistica.painel',
        label: 'Painel de Logística',
        icon: 'bi-kanban',
        path: '/estimativa',
        roles: ['dev', 'admin', 'gGeral', 'documental', 'supDoc', 'avaria', 'supAva', 'financeiro'],
      },
      {
        key: 'logistica.painel_vistorias',
        label: 'Painel de Vistorias',
        icon: 'bi-card-checklist',
        path: '/painel_vistorias',
        roles: ['dev', 'admin', 'gGeral', 'documental', 'supDoc', 'avaria', 'supAva'],
      },
      {
        // Só leitura dos embarques que o app Flutter grava em
        // operacional.embarques. Papéis: quem já acompanha a operação de saída.
        key: 'logistica.embarques',
        label: 'Embarques Realizados',
        icon: 'bi-box-seam',
        path: '/embarques',
        roles: ['dev', 'admin', 'gGeral', 'supDoc', 'documental', 'agendamento', 'gerencPatio'],
      },
      {
        key: 'logistica.devolucoes',
        label: 'Devoluções',
        icon: 'bi-arrow-return-left',
        children: [
          {
            key: 'logistica.devolucoes.painel',
            label: 'Painel de Devoluções',
            icon: 'bi-columns-gap',
            path: '/painel_devolucao',
            roles: ['dev', 'admin', 'gGeral', 'documental', 'supDoc', 'agendamento', 'avaria', 'supAva'],
          },
          {
            key: 'logistica.devolucoes.consulta',
            label: 'Consulta Devoluções',
            icon: 'bi-search',
            path: '/consulta_devolucoes',
            roles: ['dev', 'admin', 'gGeral', 'supDoc', 'agendamento', 'documental'],
          },
          {
            key: 'logistica.devolucoes.janelas',
            label: 'Janelas',
            icon: 'bi-calendar-range',
            path: '/janelas_devolucao',
            roles: ['dev', 'admin', 'gGeral', 'supDoc'],
          },
          {
            key: 'logistica.devolucoes.pre_cadastro',
            label: 'Pré-Cadastro',
            icon: 'bi-pencil-square',
            path: '/pre_cadastro_devolucao',
            roles: ['dev', 'admin', 'gGeral', 'supDoc'],
          },
          {
            key: 'logistica.devolucoes.rc_suzano',
            label: 'Cadastro (RC/SZ)',
            icon: 'bi-journal-plus',
            path: '/cadastro_devolucao_rc_suzano',
            roles: ['dev', 'admin', 'gGeral', 'supDoc'],
          },
        ],
      },
    ],
  },

  {
    key: 'agendamento',
    label: 'Agendamento',
    icon: 'bi-calendar-check',
    children: [
      {
        key: 'agendamento.painel',
        label: 'Painel de Agendamento',
        icon: 'bi-calendar3',
        path: '/painel_agendamentos',
        roles: ['dev', 'admin', 'gGeral', 'supDoc', 'agendamento'],
      },
      {
        key: 'agendamento.bookings',
        label: 'Bookings',
        icon: 'bi-journal-text',
        path: '/booking',
        roles: ['dev', 'admin', 'gGeral', 'supDoc', 'agendamento'],
      },
      {
        key: 'agendamento.booking_edi',
        label: 'Booking EDI',
        icon: 'bi-file-earmark-code',
        path: '/manutencao/booking-edi',
        roles: ['dev', 'admin', 'gGeral', 'supDoc'],
      },
    ],
  },

  {
    key: 'documental',
    label: 'Documental',
    icon: 'bi-file-earmark-text',
    children: [
      {
        key: 'documental.protocolo',
        label: 'Protocolar Estimativas',
        icon: 'bi-send',
        path: '/protocolo_estimativas',
        roles: ['dev', 'admin', 'gGeral', 'supDoc', 'documental'],
      },
      {
        key: 'documental.cancelamento_expo',
        label: 'Cancelamento de Exportação',
        icon: 'bi-x-octagon',
        path: '/cancelamento-expo',
        roles: ['dev', 'admin', 'gGeral', 'supDoc', 'documental', 'agendamento'],
      },
      {
        key: 'documental.atualizacao_status',
        label: 'Atualização de Status',
        icon: 'bi-arrow-repeat',
        path: '/atualizacao_status',
        roles: ['dev', 'admin', 'gGeral', 'supDoc', 'documental'],
      },
      {
        key: 'documental.pesquisa_fotos',
        label: 'Pesquisar Fotos',
        icon: 'bi-images',
        path: '/pesquisa_fotos',
        roles: ['dev', 'admin', 'gGeral', 'supDoc', 'documental'],
      },
      {
        key: 'documental.pesquisa_fotos_avaria',
        label: 'Pesquisa de Fotos (Oficina)',
        icon: 'bi-images',
        path: '/pesquisa_fotos_avaria',
        roles: ['dev', 'admin', 'gGeral', 'supDoc', 'documental'],
      },
      {
        key: 'documental.isencoes_a_definir',
        label: 'Isenções à Definir',
        icon: 'bi-hourglass-split',
        path: '/isencoes_a_definir',
        roles: ['dev', 'admin', 'gGeral', 'supDoc', 'documental'],
      },
      {
        key: 'documental.no_show',
        label: 'No Show',
        icon: 'bi-person-x',
        path: '/no_show',
        roles: ['dev', 'admin', 'gGeral', 'supDoc'],
      },
      {
        key: 'documental.parceiros',
        label: 'Parceiros',
        icon: 'bi-people',
        path: '/parceiros',
        hidden: true,
        roles: ['dev', 'admin', 'gGeral', 'supDoc', 'documental'],
      },
    ],
  },

  {
    key: 'ti',
    label: 'T.I',
    icon: 'bi-pc-display',
    children: [
      {
        key: 'ti.celulares',
        label: 'Controle Celulares',
        icon: 'bi-phone',
        path: '/controle_celulares',
        roles: ['dev', 'admin', 'gGeral'],
      },
      {
        key: 'ti.computadores',
        label: 'Controle Computadores',
        icon: 'bi-laptop',
        path: '/controle_computadores',
        roles: ['dev', 'admin', 'gGeral'],
      },
      {
        key: 'ti.impressora',
        label: 'Teste de Impressora',
        icon: 'bi-printer',
        path: '/teste_impressora',
        roles: ['dev', 'admin', 'gGeral'],
      },
    ],
  },

  {
    key: 'relatorios',
    label: 'Relatórios',
    icon: 'bi-file-earmark-bar-graph',
    children: [
      {
        key: 'relatorios.dashboards_gerenciais',
        label: 'Dashboards Gerenciais',
        icon: 'bi-speedometer2',
        path: '/dashboards/gerenciais',
        roles: ['dev', 'admin', 'gGeral'],
      },
      {
        key: 'relatorios.agendamentos',
        label: 'Relatório de Agendamentos',
        icon: 'bi-calendar-week',
        path: '/relatorio_agendamentos',
        roles: ['dev', 'admin', 'gGeral', 'supDoc'],
      },
      {
        key: 'relatorios.produtividade',
        label: 'Produtividade Colaboradores',
        icon: 'bi-graph-up',
        path: '/produtividade_agendamentos',
        roles: ['dev', 'admin', 'gGeral'],
      },
      {
        key: 'relatorios.produtividade_vistorias',
        label: 'Produtividade de Cria',
        icon: 'bi-clipboard2-data',
        path: '/produtividade_vistorias',
        roles: ['dev', 'admin', 'gGeral'],
      },
      {
        key: 'relatorios.dashboard_devolucoes',
        label: 'Dashboard Devoluções',
        icon: 'bi-bar-chart',
        path: '/dashboard_devolucoes',
        roles: ['dev', 'admin', 'gGeral', 'supDoc'],
      },
      {
        key: 'relatorios.dashboard_avaria',
        label: 'Dashboard de Avarias',
        icon: 'bi-pie-chart',
        path: '/dashboard_avaria',
        roles: ['dev', 'admin', 'gGeral', 'supAva'],
      },
      {
        key: 'relatorios.vistoria_status',
        label: 'Relatório de Vistorias',
        icon: 'bi-clipboard-data',
        path: '/relatorio_vistoria_status',
        roles: ['dev', 'admin', 'gGeral', 'supAva'],
      },
      {
        key: 'relatorios.saidas_agendamentos',
        label: 'Saídas de Agendamentos',
        icon: 'bi-box-arrow-right',
        path: '/saidas_agendamentos',
        roles: ['dev', 'admin', 'gGeral', 'supDoc'],
      },
      {
        key: 'relatorios.inventario',
        label: 'Inventário',
        icon: 'bi-boxes',
        path: '/inventario',
        roles: ['dev', 'admin', 'gGeral', 'documental', 'supDoc', 'supAva'],
      },
      {
        key: 'relatorios.movimentacao',
        label: 'Movimentação',
        icon: 'bi-arrow-left-right',
        roles: ['dev', 'admin', 'gGeral', 'documental', 'supDoc', 'supAva'],
        children: [
          {
            key: 'relatorios.movimentacao.in',
            label: 'Entradas (IN)',
            path: '/relatorios/movimentacao/in',
            roles: ['dev', 'admin', 'gGeral', 'documental', 'supDoc', 'supAva'],
          },
          {
            key: 'relatorios.movimentacao.out',
            label: 'Saídas (OUT)',
            path: '/relatorios/movimentacao/out',
            roles: ['dev', 'admin', 'gGeral', 'documental', 'supDoc', 'supAva'],
          },
        ],
      },
      {
        key: 'relatorios.maersk_ordens',
        label: 'Ordens Maersk (AEMS)',
        icon: 'bi-list-check',
        path: '/relatorios/maersk-ordens',
        roles: ['dev', 'admin', 'gGeral', 'documental', 'supDoc'],
      },
      {
        key: 'relatorios.lista',
        label: 'Lista de Relatórios',
        icon: 'bi-list-ul',
        path: '/relatorios',
        hidden: true,
        roles: ['dev', 'admin', 'gGeral', 'documental', 'supDoc', 'avaria', 'supAva'],
      },
    ],
  },

  {
    key: 'faturamento',
    label: 'Faturamento',
    icon: 'bi-receipt',
    children: [
      {
        key: 'faturamento.armadores',
        label: 'Gestão de Armadores',
        icon: 'bi-buildings',
        path: '/faturamento/armadores',
        roles: ['dev', 'admin', 'gGeral', 'financeiro'],
      },
      {
        key: 'faturamento.geracao',
        label: 'Geração de Faturamento',
        icon: 'bi-play-circle',
        path: '/faturamento/geracao',
        roles: ['dev', 'admin', 'gGeral', 'financeiro'],
      },
      {
        key: 'faturamento.consulta',
        label: 'Consulta de Faturas Geradas',
        icon: 'bi-search',
        path: '/faturamento/consulta',
        roles: ['dev', 'admin', 'gGeral', 'financeiro'],
      },
      {
        key: 'faturamento.conferencia',
        label: 'Conferência do Armador',
        icon: 'bi-check2-square',
        path: '/faturamento/conferencia',
        roles: ['dev', 'admin', 'gGeral', 'financeiro'],
      },
      {
        key: 'faturamento.faturas',
        label: 'Faturas Confirmadas',
        icon: 'bi-file-earmark-check',
        path: '/faturamento/faturas',
        roles: ['dev', 'admin', 'gGeral', 'financeiro'],
      },
    ],
  },

  {
    key: 'gerenciar',
    label: 'Gerenciar',
    icon: 'bi-tools',
    children: [
      {
        key: 'gerenciar.validar_chancela',
        label: 'Validar Chancela (EIR)',
        icon: 'bi-patch-check',
        path: '/manutencao/validar-chancela',
        roles: ['dev', 'admin', 'gGeral', 'supDoc', 'supAva', 'avaria', 'documental', 'gerencPatio'],
      },
      {
        key: 'gerenciar.lacres',
        label: 'Gerenciamento de Lacres',
        icon: 'bi-lock',
        path: '/gerenciamento_lacres',
        roles: ['dev', 'admin', 'gGeral', 'supDoc', 'documental'],
      },
      {
        // Publicar já aprova (2026-09-17): Documental perdeu acesso, só
        // Gerência de Pátio publica além de admin/gGeral/dev.
        key: 'gerenciar.saldos_patios',
        label: 'Saldos dos Pátios',
        icon: 'bi-clipboard-data',
        path: '/saldos_patios',
        roles: ['dev', 'admin', 'gGeral', 'gerencPatio'],
      },
      {
        key: 'gerenciar.config_patio',
        label: 'Configuração de Pátio',
        icon: 'bi-sliders',
        path: '/config_patio',
        roles: ['dev', 'admin', 'gGeral', 'supDoc', 'gerencPatio'],
      },
      {
        key: 'gerenciar.usuarios',
        label: 'Usuários',
        icon: 'bi-people',
        path: '/usuarios',
        roles: ['dev', 'admin', 'gGeral', 'supDoc', 'supAva'],
      },
      {
        key: 'gerenciar.bloqueios',
        label: 'Bloqueios',
        icon: 'bi-slash-circle',
        children: [
          {
            key: 'gerenciar.bloqueios.motoristas',
            label: 'Bloqueio Motoristas',
            path: '/manutencao/bloqueio-motoristas',
            roles: ['dev', 'admin', 'gGeral', 'supDoc'],
          },
          {
            key: 'gerenciar.bloqueios.transportadoras',
            label: 'Bloqueio Transportadoras',
            path: '/manutencao/bloqueio-transportadoras',
            roles: ['dev', 'admin', 'gGeral', 'supDoc'],
          },
          {
            key: 'gerenciar.bloqueios.importadores',
            label: 'Bloqueio Importadores',
            path: '/avaria/bloqueio-importadores',
            roles: ['dev', 'admin', 'gGeral', 'avaria', 'supAva'],
          },
        ],
      },
      {
        key: 'gerenciar.tabelas_avarias',
        label: 'Tabelas de Avarias',
        icon: 'bi-table',
        path: '/manutencao/tabelas-armadores',
        roles: ['dev', 'admin', 'gGeral'],
      },
      {
        key: 'gerenciar.tabela_destinos',
        label: 'Tabela de Destinos',
        icon: 'bi-geo-alt',
        path: '/manutencao/tabela-destinos',
        roles: ['dev', 'admin', 'gGeral', 'supDoc', 'supAva', 'documental', 'avaria'],
      },
      {
        key: 'gerenciar.codigos_equivalencia',
        label: 'Códigos Equivalência',
        icon: 'bi-shuffle',
        path: '/manutencao/codigos-equivalencia',
        roles: ['dev', 'admin', 'gGeral'],
      },
      {
        // Logs do Sistema (schema logs): quem fez o quê. Seed em ddl_rbac_05_auditoria.sql;
        // a API (auditoria_service.TIPOS_USUARIO_AUDITORIA) trava os mesmos 3 papéis.
        key: 'gerenciar.auditoria',
        label: 'Auditoria',
        icon: 'bi-shield-check',
        path: '/auditoria',
        roles: ['dev', 'admin', 'gGeral'],
      },
      {
        // Fila do dual-write pátio→web (operacional.sync_pendencias). Seed em
        // ddl_rbac_07_sync_pendencias.sql; a API (sync_web_service.TIPOS_USUARIO_SYNC)
        // trava os mesmos 3 papéis.
        key: 'gerenciar.sync_pendencias',
        label: 'Sincronização Web',
        icon: 'bi-arrow-repeat',
        path: '/sync_pendencias',
        roles: ['dev', 'admin', 'gGeral'],
      },
      {
        key: 'gerenciar.direcionamento',
        label: 'Direcionamento de Áreas',
        icon: 'bi-signpost-split',
        path: '/manutencao/direcionamento-areas',
        hidden: true,
        roles: ['dev', 'admin', 'gGeral'],
      },
    ],
  },

  {
    key: 'config',
    label: 'Configurações',
    icon: 'bi-gear',
    children: [
      {
        key: 'config.manutencao',
        label: 'Manutenção',
        icon: 'bi-wrench',
        path: '/manutencao',
        roles: ['dev', 'admin'],
      },
      {
        key: 'config.status_sistema',
        label: 'Status do Sistema',
        icon: 'bi-heart-pulse',
        path: '/status-sistema',
        roles: ['dev'],
      },
      {
        key: 'config.dashboards',
        label: 'Dashboards e Relatórios',
        icon: 'bi-kanban',
        path: '/configuracoes/dashboards',
        roles: ['dev', 'admin', 'gGeral'],
      },
      {
        // Administração do RBAC. Exige ddl_rbac.sql rodado E a
        // operacional-api-nodejs com as rotas /rbac/* no ar; sem os dois a tela
        // abre e só mostra o erro "rode ddl_rbac.sql". Ver RBAC.md §0.
        key: 'config.papeis',
        label: 'Papéis de Usuário',
        icon: 'bi-shield-lock',
        path: '/manutencao/papeis',
        roles: ['dev', 'admin'],
      },
      {
        key: 'config.desenvolvimento',
        label: 'Desenvolvimento',
        icon: 'bi-code-slash',
        path: '/desenvolvimento',
        hidden: true,
        roles: ['dev', 'admin', 'gGeral', 'supAva', 'supDoc'],
      },
      {
        key: 'config.maersk_edi',
        label: 'Maersk EDI',
        icon: 'bi-hdd-network',
        path: '/manutencao/maersk-edi',
        hidden: true,
        roles: ['dev', 'admin'],
      },
      {
        key: 'config.simulador_vistoria',
        label: 'Simulador de Vistoria',
        icon: 'bi-joystick',
        path: '/manutencao/simulador-vistoria',
        hidden: true,
        roles: ['dev', 'admin'],
      },
      {
        key: 'config.documentos_teste',
        label: 'Documentos (teste)',
        icon: 'bi-file-earmark',
        path: '/documentos_teste',
        hidden: true,
        roles: ['dev', 'admin'],
      },
      {
        key: 'config.perfil',
        label: 'Meu Perfil',
        icon: 'bi-person',
        path: '/perfil',
        hidden: true,
        roles: HOME_ROLES,
      },
      {
        // Acessível a todo usuário logado (sino "Sistema Atualizado" na
        // navbar) — não fica escondida atrás do menuKey config.manutencao
        // (dev/admin), que exige a Central de Manutenção. Publicar nota
        // nova continua restrito a dev no backend (patch_notes_service.py).
        key: 'config.notas_atualizacao',
        label: 'Notas de Atualização',
        icon: 'bi-megaphone',
        path: '/notas_atualizacao',
        hidden: true,
        roles: ALL_OPE,
      },
      {
        key: 'config.info',
        label: 'Versão do Software',
        icon: 'bi-info-circle',
        path: '/info',
        hidden: true,
        roles: ['dev', 'admin', 'gGeral'],
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Helpers derivados do MENU (percorrem a árvore uma vez).
// ---------------------------------------------------------------------------

/** Percorre a árvore chamando fn(node, parents). */
export function walkMenu(nodes = MENU, fn, parents = []) {
  nodes.forEach((node) => {
    fn(node, parents);
    if (node.children) walkMenu(node.children, fn, [...parents, node]);
  });
}

/** Lista plana de todos os nós, com `parentKey` e `depth`. */
export function flattenMenu() {
  const out = [];
  walkMenu(MENU, (node, parents) => {
    out.push({
      key: node.key,
      label: node.label,
      path: node.path || null,
      icon: node.icon || null,
      roles: node.roles || null,
      hidden: !!node.hidden,
      parentKey: parents.length ? parents[parents.length - 1].key : null,
      depth: parents.length,
      isGroup: !!node.children,
    });
  });
  return out;
}

/** key -> nó (com metadados). */
export const MENU_INDEX = flattenMenu().reduce((acc, n) => {
  acc[n.key] = n;
  return acc;
}, {});

/**
 * Permissões padrão por papel: { roleKey: Set<menuKey> }.
 * Usado como fallback quando a API de permissões não responde e como seed
 * do banco. Um GRUPO herda o acesso da união dos filhos (não precisa de
 * `roles` próprio).
 */
export function defaultPermissionsByRole() {
  const byRole = {};
  const add = (role, key) => {
    (byRole[role] || (byRole[role] = new Set())).add(key);
  };
  walkMenu(MENU, (node, parents) => {
    if (!node.roles) return;
    node.roles.forEach((role) => {
      add(role, node.key);
      // grupos ancestrais ficam visíveis automaticamente
      parents.forEach((p) => add(role, p.key));
    });
  });
  return byRole;
}

export { ALL_OPE };
export default MENU;
